import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Leaf, CameraOff, Phone, Mail, User, MapPin, FileWarning, CheckCircle, AlertCircle, Loader, PenLine } from 'lucide-react';
import './PhotoWaiver.css';

function PhotoWaiver() {
  const [searchParams] = useSearchParams();
  const prefillName = searchParams.get('name') || '';
  const prefillEmail = searchParams.get('email') || '';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Client info
  const [clientInfo, setClientInfo] = useState({
    firstName: prefillName.split(' ')[0] || '',
    lastName: prefillName.split(' ').slice(1).join(' ') || '',
    email: prefillEmail,
    phone: '',
    street: '',
    city: '',
    state: 'VA',
    zip: ''
  });

  // Acknowledgments
  const [acknowledgments, setAcknowledgments] = useState({
    understandPurpose: false,
    waivePhotos: false,
    waiveInsurance: false,
    acceptResponsibility: false
  });

  // Signature
  const [signature, setSignature] = useState('');

  // Errors
  const [errors, setErrors] = useState({});

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const validate = () => {
    const newErrors = {};

    if (!clientInfo.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!clientInfo.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!clientInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(clientInfo.email)) newErrors.email = 'Invalid email address';
    if (!clientInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!clientInfo.street.trim()) newErrors.street = 'Street address is required';
    if (!clientInfo.city.trim()) newErrors.city = 'City is required';
    if (!clientInfo.zip.trim()) newErrors.zip = 'ZIP code is required';

    if (!acknowledgments.understandPurpose) newErrors.understandPurpose = 'Required';
    if (!acknowledgments.waivePhotos) newErrors.waivePhotos = 'Required';
    if (!acknowledgments.waiveInsurance) newErrors.waiveInsurance = 'Required';
    if (!acknowledgments.acceptResponsibility) newErrors.acceptResponsibility = 'Required';

    if (!signature.trim()) {
      newErrors.signature = 'Signature is required';
    } else {
      const fullName = `${clientInfo.firstName} ${clientInfo.lastName}`.trim().toLowerCase();
      if (signature.trim().toLowerCase() !== fullName) {
        newErrors.signature = 'Signature must match your full name';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const res = await fetch('/.netlify/functions/submit-photo-waiver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: {
            firstName: clientInfo.firstName,
            lastName: clientInfo.lastName,
            email: clientInfo.email,
            phone: clientInfo.phone,
            address: {
              street: clientInfo.street,
              city: clientInfo.city,
              state: clientInfo.state,
              zip: clientInfo.zip
            }
          },
          waiver: {
            type: 'photo_waiver',
            understandPurpose: acknowledgments.understandPurpose,
            waivePhotos: acknowledgments.waivePhotos,
            waiveInsurance: acknowledgments.waiveInsurance,
            acceptResponsibility: acknowledgments.acceptResponsibility,
            signature: signature.trim(),
            signedAt: new Date().toISOString(),
            ipAddress: null // set server-side if needed
          }
        })
      });

      let body = {};
      try { body = await res.json(); } catch (_) {}

      if (!res.ok) {
        console.error('Photo waiver submission failed:', res.status, body);
        setErrors({
          submit: body?.error || 'Something went wrong submitting your waiver. Please try again or call us at (434) 207-2246.'
        });
        setIsSubmitting(false);
        return;
      }

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Failed to submit photo waiver:', error);
      setErrors({ submit: 'Failed to submit. Please try again or call us at (434) 207-2246.' });
    }

    setIsSubmitting(false);
  };

  // Success screen
  if (submitted) {
    return (
      <div className="waiver-page">
        <div className="waiver-container">
          <div className="waiver-header">
            <div className="waiver-logo">
              <div className="waiver-logo-icon"><Leaf size={24} /></div>
              <div>
                <h1>Two Sisters</h1>
                <span>Cleaning & Organizing</span>
              </div>
            </div>
          </div>

          <div className="waiver-card">
            <div className="waiver-success">
              <div className="waiver-success-icon">
                <CheckCircle size={40} />
              </div>
              <h2>Waiver Submitted</h2>
              <p>Your photo waiver has been recorded. A copy has been sent to your email for your records.</p>

              <div className="waiver-success-details">
                <h4>Waiver Summary</h4>
                <p><strong>Name:</strong> {clientInfo.firstName} {clientInfo.lastName}</p>
                <p><strong>Property:</strong> {clientInfo.street}, {clientInfo.city}, {clientInfo.state} {clientInfo.zip}</p>
                <p><strong>Date Signed:</strong> {today}</p>
                <p><strong>Status:</strong> Before/after photos declined</p>
              </div>

              <div className="waiver-success-warning">
                <AlertCircle size={20} />
                <p>By signing this waiver, you have acknowledged that you waive any rights to insurance claims that rely on before/after photo documentation of your property.</p>
              </div>
            </div>

            <div className="waiver-footer">
              <p>Questions about this waiver?</p>
              <div className="waiver-footer-contact">
                <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
                <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="waiver-page">
      <div className="waiver-container">
        {/* Header */}
        <div className="waiver-header">
          <div className="waiver-logo">
            <div className="waiver-logo-icon"><Leaf size={24} /></div>
            <div>
              <h1>Two Sisters</h1>
              <span>Cleaning & Organizing</span>
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="waiver-card">
          {/* Title */}
          <div className="waiver-title-section">
            <div className="waiver-title-icon"><CameraOff size={22} /></div>
            <h2>Photo Waiver Form</h2>
            <p>Use this form to decline before and after photography of your home. Please read the waiver carefully before signing.</p>
          </div>

          {/* Client Info */}
          <div className="waiver-client-section">
            <h3><User size={18} /> Your Information</h3>

            <div className="waiver-form-row">
              <div className="waiver-form-group">
                <label>First Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={clientInfo.firstName}
                  onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                  className={errors.firstName ? 'error' : ''}
                  placeholder="First name"
                />
                {errors.firstName && <div className="waiver-form-error">{errors.firstName}</div>}
              </div>
              <div className="waiver-form-group">
                <label>Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={clientInfo.lastName}
                  onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                  className={errors.lastName ? 'error' : ''}
                  placeholder="Last name"
                />
                {errors.lastName && <div className="waiver-form-error">{errors.lastName}</div>}
              </div>
            </div>

            <div className="waiver-form-row">
              <div className="waiver-form-group">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                  className={errors.email ? 'error' : ''}
                  placeholder="email@example.com"
                />
                {errors.email && <div className="waiver-form-error">{errors.email}</div>}
              </div>
              <div className="waiver-form-group">
                <label>Phone <span className="required">*</span></label>
                <input
                  type="tel"
                  value={clientInfo.phone}
                  onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                  className={errors.phone ? 'error' : ''}
                  placeholder="(555) 555-5555"
                />
                {errors.phone && <div className="waiver-form-error">{errors.phone}</div>}
              </div>
            </div>

            <div className="waiver-form-group">
              <label><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Street Address <span className="required">*</span></label>
              <input
                type="text"
                value={clientInfo.street}
                onChange={(e) => setClientInfo({ ...clientInfo, street: e.target.value })}
                className={errors.street ? 'error' : ''}
                placeholder="123 Main St"
              />
              {errors.street && <div className="waiver-form-error">{errors.street}</div>}
            </div>

            <div className="waiver-form-row-3">
              <div className="waiver-form-group">
                <label>City <span className="required">*</span></label>
                <input
                  type="text"
                  value={clientInfo.city}
                  onChange={(e) => setClientInfo({ ...clientInfo, city: e.target.value })}
                  className={errors.city ? 'error' : ''}
                  placeholder="Charlottesville"
                />
                {errors.city && <div className="waiver-form-error">{errors.city}</div>}
              </div>
              <div className="waiver-form-group">
                <label>State</label>
                <select
                  value={clientInfo.state}
                  onChange={(e) => setClientInfo({ ...clientInfo, state: e.target.value })}
                >
                  <option value="VA">VA</option>
                  <option value="WV">WV</option>
                  <option value="MD">MD</option>
                  <option value="NC">NC</option>
                  <option value="DC">DC</option>
                </select>
              </div>
              <div className="waiver-form-group">
                <label>ZIP <span className="required">*</span></label>
                <input
                  type="text"
                  value={clientInfo.zip}
                  onChange={(e) => setClientInfo({ ...clientInfo, zip: e.target.value })}
                  className={errors.zip ? 'error' : ''}
                  placeholder="22901"
                />
                {errors.zip && <div className="waiver-form-error">{errors.zip}</div>}
              </div>
            </div>
          </div>

          {/* Waiver Text */}
          <div className="waiver-content">
            <h3><FileWarning size={18} /> Waiver of Before/After Photography</h3>

            <div className="waiver-text">
              <p>Two Sisters Cleaning & Organizing LLC ("the Company") routinely takes before and after photographs of client properties as part of our standard service process. These photographs serve as documentation for:</p>

              <ol>
                <li><strong>Quality assurance</strong> — verifying the condition of the home before and after each cleaning service.</li>
                <li><strong>Insurance documentation</strong> — providing visual evidence of the property's condition prior to and following our services, which can be used to support or defend claims related to property damage, pre-existing conditions, or service disputes.</li>
                <li><strong>Accountability</strong> — protecting both the client and the Company in the event of any disagreements regarding the condition of the home.</li>
              </ol>

              <div className="waiver-highlight">
                <p><strong>By signing this waiver, you are electing to decline before and after photography of your property.</strong></p>
                <p>You understand and acknowledge that without photographic documentation, the Company will have limited ability to verify pre-existing conditions, and you will be unable to use before/after photos as evidence to support any insurance claims or property damage disputes related to our services.</p>
              </div>

              <p>Specifically, by signing this waiver you acknowledge and agree to the following:</p>

              <ol>
                <li>You <strong>voluntarily decline</strong> before and after photography of your property by Two Sisters Cleaning & Organizing LLC.</li>
                <li>You understand that before/after photos serve as critical documentation for insurance purposes and dispute resolution.</li>
                <li>You <strong>waive any and all rights</strong> to file insurance claims or property damage claims against Two Sisters Cleaning & Organizing LLC that would rely on before/after photographic evidence as documentation or proof.</li>
                <li>You acknowledge that without photographic records, the Company cannot verify or be held responsible for pre-existing damage, wear, or conditions in your home.</li>
                <li>You accept full responsibility for documenting the condition of your property before and after each service, should you wish to maintain personal records.</li>
                <li>This waiver remains in effect for all future services unless revoked in writing by contacting Two Sisters Cleaning & Organizing LLC.</li>
              </ol>

              <p>This waiver does not affect any other terms of your service agreement with Two Sisters Cleaning & Organizing LLC. All other terms and conditions remain in full effect.</p>
            </div>
          </div>

          {/* Acknowledgment Checkboxes */}
          <div className="waiver-acknowledgment">
            <h3>Acknowledgment & Agreement</h3>

            <div className={`waiver-checkbox ${errors.understandPurpose ? 'error' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledgments.understandPurpose}
                  onChange={(e) => setAcknowledgments({ ...acknowledgments, understandPurpose: e.target.checked })}
                />
                <span className="checkmark"></span>
                <span>I understand that before/after photos are taken for quality assurance, insurance documentation, and accountability purposes. *</span>
              </label>
            </div>

            <div className={`waiver-checkbox ${errors.waivePhotos ? 'error' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledgments.waivePhotos}
                  onChange={(e) => setAcknowledgments({ ...acknowledgments, waivePhotos: e.target.checked })}
                />
                <span className="checkmark"></span>
                <span>I voluntarily decline before and after photography of my property by Two Sisters Cleaning & Organizing LLC. *</span>
              </label>
            </div>

            <div className={`waiver-checkbox ${errors.waiveInsurance ? 'error' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledgments.waiveInsurance}
                  onChange={(e) => setAcknowledgments({ ...acknowledgments, waiveInsurance: e.target.checked })}
                />
                <span className="checkmark"></span>
                <span>I waive any and all rights to file insurance claims or property damage claims against Two Sisters Cleaning & Organizing LLC that would rely on before/after photographic evidence. *</span>
              </label>
            </div>

            <div className={`waiver-checkbox ${errors.acceptResponsibility ? 'error' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={acknowledgments.acceptResponsibility}
                  onChange={(e) => setAcknowledgments({ ...acknowledgments, acceptResponsibility: e.target.checked })}
                />
                <span className="checkmark"></span>
                <span>I accept full responsibility for documenting the condition of my property before and after each service, and I acknowledge the Company cannot verify pre-existing conditions without photos. *</span>
              </label>
            </div>
          </div>

          {/* Signature */}
          <div className="waiver-signature">
            <h3><PenLine size={18} /> Digital Signature</h3>
            <p style={{ fontSize: '0.88rem', color: '#6b7280', marginBottom: 12 }}>
              Please type your full name exactly as it appears above to sign this waiver.
            </p>
            <div className="signature-input-wrapper">
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className={errors.signature ? 'error' : ''}
                placeholder={`${clientInfo.firstName} ${clientInfo.lastName}`.trim() || 'Your full name'}
              />
              <div className="signature-line">Client Signature</div>
            </div>
            {errors.signature && <div className="waiver-form-error">{errors.signature}</div>}
            <div className="waiver-date"><strong>Date:</strong> {today}</div>
          </div>

          {/* Submit */}
          {errors.submit && (
            <div className="submit-error">
              <AlertCircle size={20} />
              {errors.submit}
            </div>
          )}

          <button
            type="button"
            className="waiver-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader size={20} className="spinning" />
                Submitting Waiver...
              </>
            ) : (
              <>
                Sign & Submit Waiver <PenLine size={20} />
              </>
            )}
          </button>

          {/* Footer */}
          <div className="waiver-footer">
            <p>Questions about this waiver?</p>
            <div className="waiver-footer-contact">
              <a href="tel:+14342072246"><Phone size={16} /> (434) 207-2246</a>
              <a href="mailto:hello@twosisterscleaningco.com"><Mail size={16} /> hello@twosisterscleaningco.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoWaiver;
